import type { FC } from "react";

import { translate } from "app/filters/i18nFilter";
import { MetricFormat } from "app/common/benchmark/types";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { NoData } from "pro-features/components/NoData/src/NoData";

import GenericWidgetSearchOverview from "../components/GenericWidget/GenericWidgetSearchOverview";
import useTrafficSearchApiParams from "../hooks/useApiParamsSearch";
import { useTrafficData } from "../services/search-overview-api/apiHooks";

import RankingAxis from "./components/RankingAxis";
import { LINE_BACKGROUND_COLOR, OVERVIEW_PPC_TEXT, USD_SIGN } from "./consts";
import { useTransformedTrafficData } from "./helpers/useTransformedTrafficData";
import { RankingContainer, RowWrapper, SearchOverviewPpcSpendWrapper, Title } from "./StyledComponents";
import { getDomains } from "./utils";

interface ISearchOverviewPpcSpend {
    contentHeight?: string;
}

const SearchOverviewPpcSpend: FC<ISearchOverviewPpcSpend> = ({ contentHeight }) => {
    const { trafficSearchApiParams } = useTrafficSearchApiParams();

    const domains = getDomains();

    const { data: trafficData, isLoading, isError } = useTrafficData(trafficSearchApiParams);

    const { paidVisitsData, ppcSpendData, costPerVisitData } = useTransformedTrafficData(trafficData, domains);

    const rankingData = [
        {
            title: OVERVIEW_PPC_TEXT.paidVisits,
            data: paidVisitsData,
            format: MetricFormat.numberAbbr,
            tooltipValueTitle: OVERVIEW_PPC_TEXT.paidVisits,
            lineBackgroundColor: LINE_BACKGROUND_COLOR.positive,
        },
        {
            title: OVERVIEW_PPC_TEXT.ppcSpend,
            data: ppcSpendData,
            format: MetricFormat.numberAbbr,
            tooltipValueTitle: OVERVIEW_PPC_TEXT.ppcSpend,
            prefix: USD_SIGN,
            lineBackgroundColor: LINE_BACKGROUND_COLOR.positive,
        },
        {
            title: OVERVIEW_PPC_TEXT.costPerVisit,
            data: costPerVisitData,
            format: MetricFormat.number,
            tooltipValueTitle: OVERVIEW_PPC_TEXT.costPerVisit,
            prefix: USD_SIGN,
            lineBackgroundColor: LINE_BACKGROUND_COLOR.negative,
            decimal: 2,
        },
    ];

    return (
        <SearchOverviewPpcSpendWrapper data-automation="search-overview-ppc-spend">
            <GenericWidgetSearchOverview
                title={OVERVIEW_PPC_TEXT.title}
                tooltip={OVERVIEW_PPC_TEXT.tooltip}
                minHeight={252}
            >
                <RankingContainer height={contentHeight}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isError ? (
                        <NoData
                            title={translate("global.nodata.notavilable")}
                            subtitle=""
                            titleSize="14px"
                        />
                    ) : (
                        rankingData.map((item) => (
                            <RowWrapper key={item.title}>
                                <Title>{item.title}</Title>
                                <RankingAxis
                                    data={item.data}
                                    format={item.format}
                                    tooltipValueTitle={item.tooltipValueTitle}
                                    prefix={item?.prefix}
                                    lineBackgroundColor={item.lineBackgroundColor}
                                    decimal={item?.decimal}
                                />
                            </RowWrapper>
                        ))
                    )}
                </RankingContainer>
            </GenericWidgetSearchOverview>
        </SearchOverviewPpcSpendWrapper>
    );
};

export default SearchOverviewPpcSpend;
